import { useRef } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ChefHat,
  ShoppingCart,
  Globe,
  MessageCircle,
  Check,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { LANDING } from "@/constants/testIds/landing";
import { useSiteContent } from "@/lib/siteContent";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const SECTION_ICONS = [Sparkles, ChefHat, ShoppingCart];
const SECTION_TEST_IDS = [
  LANDING.featureShopping,
  LANDING.featureRecipes,
  LANDING.featurePlanning,
];
const PERK_ICONS = { Globe, MessageCircle };

function DownloadButton({ href, label, testId, variant = "primary" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 font-semibold transition-all duration-200 hover:-translate-y-0.5";
  const styles =
    variant === "primary"
      ? "bg-[#6B8E23] text-white hover:bg-[#55711C] shadow-md hover:shadow-lg"
      : "bg-white text-[#1A1A1A] border border-[#E5E5E5] hover:border-[#6B8E23] hover:text-[#6B8E23]";
  return (
    <a
      href={href || "#"}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`${base} ${styles}`}
    >
      {label} <ArrowRight size={18} />
    </a>
  );
}

export default function LandingPage() {
  const { content } = useSiteContent();
  const heroRef = useRef(null);

  const scrollToHero = () => {
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const download = content.download_link;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]">
      <Header onCtaClick={scrollToHero} />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 sm:pt-36 sm:pb-28 overflow-hidden"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-32 -left-40 w-[480px] h-[480px] rounded-full bg-[#E8EFE1] blur-3xl opacity-70" />
          <div className="absolute top-40 -right-32 w-[420px] h-[420px] rounded-full bg-[#F4ECD8] blur-3xl opacity-60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E5E5E5] text-xs font-medium text-[#6B8E23] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6B8E23]" />
              {content.hero.badge}
            </span>

            <h1
              data-testid={LANDING.heroHeading}
              className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            >
              {content.hero.heading_line1}
              <br />
              <span className="text-[#6B8E23]">{content.hero.heading_line2}</span>
            </h1>

            <p
              data-testid={LANDING.heroSubheading}
              className="mt-6 text-base sm:text-lg text-[#555] leading-relaxed max-w-xl"
            >
              {content.hero.subheading}
            </p>

            <div className="mt-8">
              <DownloadButton
                href={download}
                label={content.hero.cta_button}
                testId={LANDING.heroSubmitButton}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden border border-[#E5E5E5] shadow-xl bg-white">
              <img
                src={content.hero.image_url}
                alt={content.hero.image_alt}
                data-testid={LANDING.heroImage}
                className="w-full h-[420px] sm:h-[520px] object-cover"
                loading="eager"
              />
            </div>

            <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white border border-[#E5E5E5] rounded-2xl shadow-lg px-5 py-4 items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E8EFE1] text-[#6B8E23]">
                <Sparkles size={20} />
              </span>
              <div>
                <p className="text-xs text-[#888]">KI-Rezeptgenerator</p>
                <p className="font-semibold text-sm text-[#1A1A1A]">
                  10 Rezepte auf Knopfdruck
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURE SECTIONS */}
      <section id="features" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-sm font-semibold text-[#6B8E23] uppercase tracking-wider mb-3">
              Funktionen
            </p>
            <h2 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              {content.features_heading}
            </h2>
            <p className="mt-4 text-[#666] text-base sm:text-lg leading-relaxed">
              {content.features_subheading}
            </p>
          </motion.div>

          <div className="mt-16 space-y-12">
            {content.sections.map((section, idx) => {
              const Icon = SECTION_ICONS[idx] || SECTION_ICONS[0];
              const testId = SECTION_TEST_IDS[idx] || `feature-section-${idx}`;
              return (
                <motion.div
                  key={idx}
                  data-testid={testId}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-3xl border border-[#EAEAEA] bg-[#FAFAFA] p-6 sm:p-10"
                >
                  <div className="flex items-center gap-4 mb-7">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#6B8E23] text-white shrink-0">
                      <Icon size={22} />
                    </span>
                    <div className="flex items-baseline gap-3">
                      <span className="font-[Outfit] text-2xl font-bold text-[#C9D6B5]">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-[Outfit] text-xl sm:text-2xl font-semibold">
                        {section.title}
                      </h3>
                    </div>
                  </div>

                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E8EFE1] text-[#6B8E23] shrink-0 mt-0.5">
                          <Check size={14} strokeWidth={3} />
                        </span>
                        <p className="text-sm text-[#555] leading-relaxed">
                          <span className="font-semibold text-[#1A1A1A]">
                            {item.lead}:
                          </span>{" "}
                          {item.text}
                        </p>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section id="perks" className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            {...fadeUp}
            className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight text-center mb-12"
          >
            {content.perks_heading}
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {content.perks.map((perk, idx) => {
              const Icon = PERK_ICONS[perk.icon] || Globe;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white rounded-3xl border border-[#EAEAEA] p-7 flex items-start gap-4"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#E8EFE1] text-[#6B8E23] shrink-0">
                    <Icon size={22} />
                  </span>
                  <div>
                    <h3 className="font-[Outfit] text-lg font-semibold mb-1">
                      {perk.title}
                    </h3>
                    <p className="text-sm text-[#666] leading-relaxed">
                      {perk.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-3xl bg-[#1A1A1A] text-white p-10 sm:p-16 text-center"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#6B8E23]/30 blur-3xl pointer-events-none" />
            <h2 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight max-w-3xl mx-auto">
              {content.final_cta_heading}
            </h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">
              {content.final_cta_subheading}
            </p>
            <div className="mt-8 flex justify-center">
              <DownloadButton
                href={download}
                label={content.final_cta_button}
                testId={LANDING.finalCtaButton}
              />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
